import dayjs, { Dayjs } from "dayjs";
import { Booking } from "../types/booking";

export const AGENT_PICKUP_DATE_FILTERS = [
  { value: "overdue", label: "Overdue (goods ready date passed)" },
  { value: "due_soon", label: "Due within 7 days" },
  { value: "upcoming", label: "Upcoming (future goods ready)" },
  { value: "awaiting_warehouse", label: "Awaiting warehouse receipt" },
  { value: "received", label: "Received in warehouse" },
  { value: "no_pickup_date", label: "No goods ready date" },
] as const;

export type AgentPickupDateFilter =
  (typeof AGENT_PICKUP_DATE_FILTERS)[number]["value"];

const todayStart = (): Dayjs => dayjs().startOf("day");

export const hasWarehouseReceipt = (booking: Booking): boolean =>
  !!booking.origin_received_date;

export const getPickupDay = (booking: Booking): Dayjs | null => {
  if (!booking.origin_pickup_date) return null;
  return dayjs(booking.origin_pickup_date).startOf("day");
};

/** Pickup date is in the future and goods not yet received. */
export const isUpcomingPickup = (booking: Booking): boolean => {
  if (hasWarehouseReceipt(booking)) return false;
  const pickup = getPickupDay(booking);
  return pickup != null && pickup.isAfter(todayStart(), "day");
};

/** Pickup date is today or in the past and goods not yet received. */
export const isOverduePickup = (booking: Booking): boolean => {
  if (hasWarehouseReceipt(booking)) return false;
  const pickup = getPickupDay(booking);
  return pickup != null && !pickup.isAfter(todayStart(), "day");
};

export const isDueSoonPickup = (booking: Booking): boolean => {
  if (hasWarehouseReceipt(booking)) return false;
  const pickup = getPickupDay(booking);
  if (!pickup) return false;
  const today = todayStart();
  return (
    !pickup.isBefore(today, "day") &&
    !pickup.isAfter(today.add(7, "day"), "day")
  );
};

export const matchesPickupDateFilter = (
  booking: Booking,
  filter?: AgentPickupDateFilter
): boolean => {
  if (!filter) return true;

  switch (filter) {
    case "overdue":
      return isOverduePickup(booking);
    case "upcoming":
      return isUpcomingPickup(booking);
    case "due_soon":
      return isDueSoonPickup(booking);
    case "awaiting_warehouse":
      return !hasWarehouseReceipt(booking) && getPickupDay(booking) != null;
    case "received":
      return hasWarehouseReceipt(booking);
    case "no_pickup_date":
      return getPickupDay(booking) == null;
    default:
      return true;
  }
};

export const comparePickupUrgency = (a: Booking, b: Booking): number => {
  const aReceived = hasWarehouseReceipt(a);
  const bReceived = hasWarehouseReceipt(b);
  if (aReceived !== bReceived) return aReceived ? 1 : -1;

  const aPickup = getPickupDay(a);
  const bPickup = getPickupDay(b);
  if (!aPickup && !bPickup) return 0;
  if (!aPickup) return 1;
  if (!bPickup) return -1;

  const aOverdue = isOverduePickup(a);
  const bOverdue = isOverduePickup(b);
  if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;

  return aPickup.valueOf() - bPickup.valueOf();
};

export const sortBookingsByPickupDate = (
  bookings: Booking[],
  order: "ascend" | "descend" = "ascend"
): Booking[] => {
  const sorted = [...bookings].sort(comparePickupUrgency);
  return order === "descend" ? sorted.reverse() : sorted;
};

export type PickupDisplayStatus =
  | "received"
  | "overdue"
  | "due_today"
  | "upcoming"
  | "none";

export const getPickupDisplayStatus = (booking: Booking): PickupDisplayStatus => {
  if (hasWarehouseReceipt(booking)) return "received";
  const pickup = getPickupDay(booking);
  if (!pickup) return "none";
  const today = todayStart();
  if (pickup.isBefore(today, "day")) return "overdue";
  if (pickup.isAfter(today, "day")) return "upcoming";
  return "due_today";
};

export const getPickupDaysFromToday = (booking: Booking): number | null => {
  const pickup = getPickupDay(booking);
  if (!pickup) return null;
  return pickup.diff(todayStart(), "day");
};
